import type { ArrowType } from './ArrowSection';

interface ReactionArrowProps {
  arrowType: ArrowType;
  width?: number;
  height?: number;
  strokeWidth?: number; // Stroke width in pixels
  className?: string;
  style?: React.CSSProperties;
  /** Accessibility label describing the arrow and its context (reagents/conditions) */
  ariaLabel?: string;
}

/**
 * Shared reaction arrow component used in both screen display and export.
 * Renders a filled triangle arrow with polygon-based arrowheads.
 * The viewBox width scales with the arrow width to maintain constant height.
 * Arrowheads are positioned outside the line length.
 */
export function ReactionArrow({
  arrowType,
  width = 48,
  height = 32,
  strokeWidth = 10,
  className,
  style,
  ariaLabel,
}: ReactionArrowProps) {
  // Grow arrowhead geometry with shaft thickness so head and line stay visually matched.
  const headScale = strokeWidth / 10;
  const arrowheadLength = 30 * headScale;
  const arrowheadHalfHeight = 14 * headScale;

  // Scale viewBox width proportionally to maintain aspect ratio for height
  // Base: 48px width = 100 viewBox units, add padding for arrowheads
  const baseViewBoxWidth = (width / 48) * 100 + 100;
  const arrowheadPadding = arrowheadLength; // Add space for arrowheads to extend beyond
  const viewBoxWidth = baseViewBoxWidth + (arrowheadPadding - arrowheadLength);

  // Fit arrowhead + shaft (plus small breathing room).
  const viewBoxHeight = (arrowheadHalfHeight * 2) + 3 + 1;

  // Arrowhead tip positions (extending beyond base width)
  const leftArrowTipX = arrowheadPadding;
  const rightArrowTipX = baseViewBoxWidth + arrowheadPadding;

  // Line positions (excluding arrowheads)
  // For double arrow: line stops at base of arrowhead on both sides
  // For single arrow: line extends to left edge (no arrowhead), stops at base of right arrowhead
  const lineStartX = arrowType === 'double' ? leftArrowTipX + arrowheadLength : leftArrowTipX;
  const lineEndX = rightArrowTipX - arrowheadLength;

  // Center position in viewBox
  const centerY = viewBoxHeight / 2;
  const viewBoxStartX = arrowheadLength;

  // Build accessibility title based on arrow type
  const arrowTitle = arrowType === 'double' ? 'Resonance Arrow' : 'Reaction Arrow';

  return (
    <svg
      width={width}
      height={height}
      viewBox={`${viewBoxStartX} 0 ${viewBoxWidth} ${viewBoxHeight}`}
      fill="black"
      className={className}
      style={style}
      role="img"
      aria-label={ariaLabel ?? arrowTitle}
    >
      {/* Accessibility metadata for SVG export */}
      <title>{arrowTitle}</title>
      {ariaLabel && <desc>{ariaLabel}</desc>}

      {/* Arrow shaft - thick black line (excludes arrowheads) */}
      <line
        x1={lineStartX}
        y1={centerY}
        x2={lineEndX}
        y2={centerY}
        stroke="black"
        strokeWidth={strokeWidth}
      />

      {/* Right arrowhead - longer and skinnier polygon */}
      <polygon
        points={`${rightArrowTipX},${centerY} ${rightArrowTipX - arrowheadLength},${centerY - arrowheadHalfHeight} ${rightArrowTipX - arrowheadLength},${centerY + arrowheadHalfHeight}`}
        fill="black"
      />

      {/* Left arrowhead (only for double) - longer and skinnier polygon */}
      {arrowType === 'double' && (
        <polygon
          points={`${leftArrowTipX},${centerY} ${leftArrowTipX + arrowheadLength},${centerY - arrowheadHalfHeight} ${leftArrowTipX + arrowheadLength},${centerY + arrowheadHalfHeight}`}
          fill="black"
        />
      )}
    </svg>
  );
}
